import React from "react";

import PropTypes from "prop-types";

import { DISCOUNT_LINE_ITEMS } from "Constants/constants";
import { formatAsCurrency } from "Libs/utils";

import StandardUserMgmtMsg from "../StandardUserMgmtMsg";

import {
  OrderDetailSS,
  OrderInfoSS,
  OrderInfoBlockSS,
  OrderInfoHeaderSS,
  OrderInfoLineSS,
  OrderPreTotalSS,
  OrderPreTotalCurrencySS,
  OrderPreTotalItemSS,
  OrderPreTotalLabelSS,
  OrderTotalSS,
  OrderTotalAmountSS,
  OrderTotalsContainerSS,
  OrderTotalInfoSS,
  OrderTotalLabelSS,
  Loading
} from "./OrderDetail.styles";

const OrderDetail = ({ loading, order, billingCycle }) => {
  if (loading) {
    return <Loading />;
  }

  return (
    <OrderDetailSS>
      {order && (
        <>
          <OrderTotals
            components={order.components}
            currency={order.components.base_price.currency}
          />
          <OrderInfo
            order={order}
            billingCycle={billingCycle}
            showPDF={false}
          />
        </>
      )}
    </OrderDetailSS>
  );
};

OrderDetail.propTypes = {
  order: PropTypes.object,
  loading: PropTypes.bool,
  showLink: PropTypes.bool,
  billingCycle: PropTypes.string,
  organizationId: PropTypes.string
};

const geSortedLineItems = components => {
  const isTax = str => str.startsWith("tax|") || str.startsWith("vat|");
  const isDiscount = str => DISCOUNT_LINE_ITEMS.includes(str);
  const sortOnKey = k => {
    return (a, b) => {
      a = a[k];
      b = b[k];
      return a - b;
    };
  };

  const getSortedKey = x => {
    if (isTax(x)) {
      return "4" + x;
    }
    if (isDiscount(x)) {
      return "3" + x;
    }
    if (x === "base_price") {
      return "1" + x;
    }
    return "0" + x;
  };

  const sortedComponents = Object.entries(components)
    .map(([k, v]) => {
      return {
        key: getSortedKey(k),
        ...v
      };
    })
    .sort(sortOnKey("key"))
    .map(x => {
      return {
        ...x,
        key: x.key.slice(1)
      };
    });

  return sortedComponents;
};

const OrderTotals = ({ components, currency }) => {
  const lineItems = geSortedLineItems(components);

  return (
    <OrderTotalsContainerSS>
      <OrderPreTotalSS>
        {lineItems.map(component => {
          const isUserManagementDiscount =
            component.key === "standard_user_management_discount";
          return (
            <OrderPreTotalItemSS key={component.display_title}>
              <OrderPreTotalLabelSS>
                {component.display_title}
                {isUserManagementDiscount && <StandardUserMgmtMsg />}
              </OrderPreTotalLabelSS>

              <OrderPreTotalCurrencySS>
                {formatAsCurrency(component.amount, component.currency)}
              </OrderPreTotalCurrencySS>
            </OrderPreTotalItemSS>
          );
        })}
      </OrderPreTotalSS>
      <OrderTotalSS>
        <OrderTotalLabelSS>Order Total</OrderTotalLabelSS>
        <OrderTotalInfoSS>
          *This amount is subject to change based on account activity
        </OrderTotalInfoSS>
        <OrderTotalAmountSS>
          {orderTotal(components, currency)}
        </OrderTotalAmountSS>
      </OrderTotalSS>
    </OrderTotalsContainerSS>
  );
};

OrderTotals.propTypes = {
  components: PropTypes.object,
  currency: PropTypes.string
};

const OrderInfo = ({ order, billingCycle }) => {
  return (
    <OrderInfoSS>
      <OrderInfoBlockSS>
        <OrderInfoHeaderSS>Billing Information</OrderInfoHeaderSS>
        <OrderInfoLineSS>{order.address?.name_line}</OrderInfoLineSS>
        <OrderInfoLineSS>{order.company}</OrderInfoLineSS>
        <OrderInfoLineSS>{order.address?.country}</OrderInfoLineSS>
      </OrderInfoBlockSS>
      {order.vat_number && (
        <OrderInfoBlockSS>
          <OrderInfoHeaderSS>EU VAT INFO: {order.vat_number}</OrderInfoHeaderSS>
        </OrderInfoBlockSS>
      )}

      <OrderInfoBlockSS>
        <OrderInfoHeaderSS>Billing cycle</OrderInfoHeaderSS>
        <OrderInfoLineSS>{billingCycle}</OrderInfoLineSS>
      </OrderInfoBlockSS>
    </OrderInfoSS>
  );
};

OrderInfo.propTypes = {
  order: PropTypes.object,
  billingCycle: PropTypes.string
};

function orderTotal(components, currency) {
  const total = Object.keys(components).reduce((total, component) => {
    return total + components[component].amount;
  }, 0);

  return formatAsCurrency(total, currency);
}

export default OrderDetail;
